import type { OrderListItem, OrderListLine } from '@agos/api-client/model'
import { isCounterCollection as counterCollection, type FilterChannel } from '@agos/shared'

/**
 * Working out which table an order belongs to, and what colour its tile is.
 *
 * The floor plan is read at a glance from across a room, so the tile's ring
 * colour is the whole interface. Getting the mapping wrong puts an order on the
 * wrong table, which is how a bill reaches the wrong guests.
 *
 * Ported from legacy `pages/table-map.tsx`.
 */

const TABLE_PREFIX = /^Stôl\s+/i

/**
 * Three places a table's name can hide, because three generations of order
 * templates wrote it differently: the column meant for it, the customer-name
 * field with a `Stôl ` prefix, and a bare number.
 */
export function tableKeyForOrder(order: OrderListItem): string | null {
  const tableName = (order as { tableName?: string | null }).tableName
  if (tableName) return tableName.replace(TABLE_PREFIX, '').trim()

  if (order.customerName && TABLE_PREFIX.test(order.customerName)) {
    return order.customerName.replace(TABLE_PREFIX, '').trim()
  }

  const tableNumber = (order as { tableNumber?: string | number | null }).tableNumber
  if (tableNumber !== null && tableNumber !== undefined && `${tableNumber}`.trim() !== '') {
    return `${tableNumber}`.trim()
  }

  return null
}

export type TableStatus = 'free' | 'in_kitchen' | 'ready' | 'served' | 'paid'

export const STATUS_RING: Record<TableStatus, string> = {
  free: 'ring-1 ring-border/40',
  in_kitchen: 'ring-2 ring-amber-500/70 shadow-amber-500/20',
  ready: 'ring-2 ring-blue-500/80 shadow-blue-500/30 animate-pulse',
  served: 'ring-2 ring-emerald-500/60 shadow-emerald-500/20',
  paid: 'ring-1 ring-zinc-500/50 opacity-60',
}

export const STATUS_BG: Record<TableStatus, string> = {
  free: '',
  in_kitchen: 'bg-amber-500/10',
  ready: 'bg-blue-500/15',
  served: 'bg-emerald-500/10',
  paid: 'bg-zinc-500/10',
}

/**
 * What the tile shows.
 *
 * The per-item served progress deliberately outranks the order's own status: a
 * waiter who has already carried everything to the table should not see the
 * tile stuck on *V kuchyni* because the kitchen flow lagged, or because some
 * flows never move an order off `new` at all.
 */
export function statusForOrder(order: OrderListItem | null): TableStatus {
  if (!order) return 'free'
  if (order.status === 'delivered') return 'paid'

  const lines = order.items ?? []
  if (lines.length > 0 && lines.every((line) => servedStatusOf(line) === 'served')) {
    return 'served'
  }

  if (order.status === 'quality_check') return 'ready'
  if (order.status === 'new' || order.status === 'preparing') return 'in_kitchen'

  // Anything else still live — on the way, at the address — has at least been
  // seen by the kitchen.
  return 'served'
}

function servedStatusOf(line: OrderListLine): string {
  return (line as { servedStatus?: string | null }).servedStatus ?? 'pending'
}

/**
 * Whether an order sits at a table - §9.473.
 *
 * <p><b>`usesTables`, not `onPremise`, and the distinction is the point.</b> A counter pickup is
 * on-premise and has no table - `isCounterOrder` below is the reader for that - so keying the floor plan
 * on `onPremise` would put every *Osobný odber* order on a table it does not have. The floor plan's
 * question is *does this order sit at a table*, which `usesTables` answers by name.
 *
 * <p><b>The fallback direction is the same decision `isCounterOrder` makes.</b> A screen whose channels
 * have not arrived yet must not empty the floor plan - a waiter looking at an empty room full of guests
 * is worse than one missing a terrace table for a second - so an unresolved channel keeps the historical
 * answer. `restaurant` has `usesTables` set, which is what makes the two agree for it.
 */
function isAtATable(type: string, channels: readonly FilterChannel[]): boolean {
  const known = channels.find((channel) => channel.slug === type)
  if (known !== undefined) return known.usesTables
  return type === 'restaurant'
}

/**
 * The one order each table is currently working.
 *
 * A table with two open orders should not happen and does: the newest wins,
 * because it is the one the waiter just took.
 *
 * <p><b>§9.473 - the channel, not the one name.</b> This read `order.type !== 'restaurant'`, so an order
 * on any other channel was **skipped** and a terrace table would have shown as free with a guest at it.
 * The owner's own example: <em>"There will be also Terasa etc"</em>.
 *
 * <p><b>And the channel list is required - §9.651, FB-980.</b> §9.473 left it optional *"so every existing
 * caller and test keeps working"*, and {@link openRoomOrders} was a caller that kept working by keeping the old
 * answer: <em>Otvorené stoly</em> called this with no channels while the floor plan beside it passed them, so a
 * terrace order was invisible on one screen and drawn on the other. A caller with no list passes an empty one and
 * says so; the fallback below is unchanged.
 */
export function ordersByTable(
  orders: OrderListItem[],
  channels: readonly FilterChannel[],
): Map<string, OrderListItem> {
  const byTable = new Map<string, OrderListItem>()

  for (const order of orders) {
    if (!isAtATable(order.type ?? '', channels)) continue
    if (order.status === 'cancelled' || order.status === 'delivered') continue

    const key = tableKeyForOrder(order)
    if (!key) continue

    const existing = byTable.get(key)
    if (!existing || createdAtOf(order) > createdAtOf(existing)) byTable.set(key, order)
  }

  return byTable
}

/**
 * Every open bill at a table, newest first per name - §9.663, FB-991.
 *
 * **`ordersByTable` answers *which order is this tile's* and has to pick one**; this answers *what is open*, which a
 * list can draw in full. The owner: *"sometimes, when they create the same názov stolu, one is shown in archiv and
 * second one is normally shown"* - and the archive was the only place left precisely because both the tiles and the
 * open-tables rows came off that map.
 *
 * The same two filters as the map, so the two cannot disagree about what *open* means: a channel that seats guests,
 * and a status that is neither cancelled nor delivered.
 */
export function openTableBills(
  orders: OrderListItem[],
  channels: readonly FilterChannel[],
): { name: string; order: OrderListItem }[] {
  const bills: { name: string; order: OrderListItem }[] = []
  for (const order of orders) {
    if (!isAtATable(order.type ?? '', channels)) continue
    if (order.status === 'cancelled' || order.status === 'delivered') continue
    const name = tableKeyForOrder(order)
    if (!name) continue
    bills.push({ name, order })
  }
  /* Newest first within a name, and names in the order their first bill appeared - so a duplicate sits directly under
     the bill it duplicates rather than wherever the server happened to list it. **A comparator returning 0 for two
     different names does not group them**: the first draft did exactly that and its own case caught the claim, which
     is why the name's position is computed rather than assumed. */
  const firstSeen = new Map<string, number>()
  bills.forEach(({ name }, position) => {
    if (!firstSeen.has(name)) firstSeen.set(name, position)
  })
  return bills.sort((left, right) =>
    left.name === right.name
      ? createdAtOf(right.order) - createdAtOf(left.order)
      : (firstSeen.get(left.name) ?? 0) - (firstSeen.get(right.name) ?? 0),
  )
}

/**
 * How many open bills each table name has - §9.663, FB-991.
 *
 * Only the names with more than one, because a tile that said *1* would be noise on every occupied table. The tile
 * draws this beside the one `ordersByTable` picked, which is the fact that was being hidden rather than a second
 * answer to *whose tile is this*.
 */
export function duplicateTableNames(
  orders: OrderListItem[],
  channels: readonly FilterChannel[],
): Map<string, number> {
  const counts = new Map<string, number>()
  for (const { name } of openTableBills(orders, channels)) {
    counts.set(name, (counts.get(name) ?? 0) + 1)
  }
  for (const [name, count] of [...counts.entries()]) {
    if (count < 2) counts.delete(name)
  }
  return counts
}

function createdAtOf(order: OrderListItem): number {
  return new Date(order.createdAt ?? 0).getTime()
}

/**
 * Pickup orders, which belong to no table. Ready first — somebody is standing
 * at the counter — then oldest.
 */
/**
 * On the premises and not at a table - §9.393.
 *
 * <p><b>`isCounterCollection` is this rule's canonical reader</b> and it is exported from
 * `@agos/shared`: the dispatch board asks it, and this screen spelled it as `type === 'bar'`, so a
 * tableless on-premise channel a franchise invents had invisible open orders in the room.
 *
 * <p><b>The wrapper exists for the fallback, and writing it without one failed seven tests.</b>
 * `isCounterCollection` answers **false** for a channel it cannot resolve - deliberately, for the
 * board, where an unresolvable channel must not be sorted as though somebody were waiting at the
 * counter. Here an empty list is the ordinary state of a screen whose channels have not arrived yet,
 * and answering false for every order empties the counter queue. So an unknown channel keeps the slug
 * check, and `bar` has both flags, which is what makes the two answers agree for it.
 */
function isCounterOrder(type: string, channels: readonly FilterChannel[]): boolean {
  const known = channels.some((channel) => channel.slug === type)
  return known ? counterCollection(type, channels) : type === 'bar'
}

export function pickupOrders(
  orders: OrderListItem[],
  channels: readonly FilterChannel[],
): OrderListItem[] {
  return orders
    .filter(
      (order) =>
        isCounterOrder(order.type ?? '', channels) &&
        order.status !== 'cancelled' &&
        order.status !== 'delivered',
    )
    .sort((left, right) => {
      const byReady = (order: OrderListItem) => (order.status === 'quality_check' ? 0 : 1)
      if (byReady(left) !== byReady(right)) return byReady(left) - byReady(right)
      return createdAtOf(left) - createdAtOf(right)
    })
}

/**
 * Everything open in the room: a card per occupied table, then the counter collections - §9.319, the
 * owner's Q9.
 *
 * **The owner:** *"In otvorené stoly also show Items for osobný odber."* **A missed port.** Legacy's
 * open-tables list is
 * `filter(o => (o.type === "restaurant" || o.type === "bar") && …)` (`restaurant.tsx:1007`) and
 * labels the pickup rows *Osobný odber* where a table would name itself (`:109`). AGOS built the
 * screen on `ordersByTable` alone, which skips anything that is not `restaurant`, so a counter order
 * and its items were on the **table map** and nowhere on the screen a waiter watches.
 *
 * Tables first and in the floor plan's own order, then the counter - a table is a person sitting in
 * the room, and the counter queue is already sorted ready-first by `pickupOrders`.
 *
 * **`heading` rather than `tableName`**, because a pickup order has no table and calling the label
 * after the thing it is not is how the hard-coded `Stôl` prefix would come back.
 */
export interface OpenRoomEntry {
  /** Stable across renders: the order's own id, not the heading, which two pickups can share. */
  key: number
  heading: string
  order: OrderListItem
  /** A counter collection, which takes no table and adds to itself differently. */
  isPickup: boolean
}

export function openRoomOrders(
  orders: OrderListItem[],
  pickupLabel: string,
  channels: readonly FilterChannel[],
): OpenRoomEntry[] {
  // §9.651 - the channels this screen already loaded, which this line used to drop. The counter half below
  // always passed them, so one feed asked the channel table and the other asked a literal.
  /* §9.663, FB-991 - **every** open bill rather than one per name. A list can draw two rows headed *Stôl 5*, which
     is honest: an operator sees both and closes one. This read `ordersByTable`, whose map keeps the newer of two, so
     the older bill was drawn on no tile **and** on no row - leaving the archive as the only place it existed. */
  const tables = openTableBills(orders, channels).map(({ name, order }) => ({
    key: order.id ?? 0,
    heading: name,
    order,
    isPickup: false,
  }))
  const counter = pickupOrders(orders, channels).map((order) => ({
    key: order.id ?? 0,
    heading: pickupLabel,
    order,
    isPickup: true,
  }))
  return [...tables, ...counter]
}

/**
 * The channels *Otvorené stoly* offers as filter tabs - `FRONTEND.md` §9.689, owner #24 (O79c).
 *
 * **The owner (#114)**: *"in Otvorené Stoly add same filter as we have in kitchen where you can select each
 * kanal"*. The same tabs and the same selection rules as the kitchen; what differs is **which channels can be
 * here at all**, because this screen draws only what {@link openRoomOrders} draws: a channel that seats guests, or
 * one handed over at the counter (`onPremise`). A delivery channel never has an open bill in the room, and a
 * channel that settles immediately is closed before it could be - a tab for either would always be empty, which on
 * a screen a waiter reads in passing is noise rather than a choice.
 *
 * **Plus any channel an open bill actually carries**, whatever its flags or switch - `offeredChannels`' rule: a
 * channel switched off stops new orders, not the ones already at a table, and a bill must never be unfilterable.
 */
export function roomFilterChannels(
  channels: readonly FilterChannel[],
  usedSlugs: readonly string[],
): FilterChannel[] {
  const used = new Set(usedSlugs)
  return channels.filter(
    (channel) =>
      used.has(channel.slug) ||
      (channel.isActive && (channel.usesTables || channel.onPremise) && !channel.settlesImmediately),
  )
}

/** `Ján Novák` → `JN`. The badge on an occupied tile. */
export function initialsOf(name: string | null | undefined): string {
  if (!name) return '?'
  return (
    name
      .split(' ')
      .map((word) => word[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  )
}

/** The floor plan's coordinate space. Tiles are positioned as percentages of it. */
export const PLANE_WIDTH = 1000
export const PLANE_HEIGHT = 600

/** Keeps a dragged tile inside the plane, whatever the pointer did. */
export function clampToPlane(
  x: number,
  y: number,
  width: number,
  height: number,
): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(PLANE_WIDTH - width, x)),
    y: Math.max(0, Math.min(PLANE_HEIGHT - height, y)),
  }
}
